import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Play, Stop, Clock as ClockIcon, Warning, X, Star, Paperclip } from "@phosphor-icons/react";
import { toast } from "sonner";
import WorkdayClock from "@/components/WorkdayClock";
import MediaGalleryModal from "@/components/MediaGalleryModal";
import TaskLogPanel from "@/components/TaskLogPanel";

function formatDuration(seconds) {
  const s = Math.max(0, Math.floor(seconds));
  const h = String(Math.floor(s / 3600)).padStart(2, "0");
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const sec = String(s % 60).padStart(2, "0");
  return `${h}:${m}:${sec}`;
}

const DISMISSED_KEY = "tk_dismissed_auto_close_report_id";

export default function StampClock() {
  const { user } = useAuth();
  const [now, setNow] = useState(new Date());
  const [objects, setObjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [active, setActive] = useState(null);
  const [selectedObject, setSelectedObject] = useState("");
  const [selectedTask, setSelectedTask] = useState("");
  const [note, setNote] = useState("");
  const [deviation, setDeviation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [autoCloseNotice, setAutoCloseNotice] = useState(null);
  // Task-file counts + open moment-bilagor för aktiv/kommande stämpling
  const [taskFileCounts, setTaskFileCounts] = useState({});
  const [taskMediaFor, setTaskMediaFor] = useState(null); // Task-objekt
  // Direkt bilaga på pågående pass (foto/film medan man arbetar)
  const [activeMediaOpen, setActiveMediaOpen] = useState(false);
  const [activeReportFileCount, setActiveReportFileCount] = useState(0);

  // Aktuellt valt objekt (för att läsa task_ids / favorite_task_ids)
  const currentObject = useMemo(
    () => objects.find((o) => o.object_id === selectedObject) || null,
    [objects, selectedObject]
  );

  // Filtrerar och sorterar moment baserat på valt objekt.
  // Favoriter visas alltid överst. Tom task_ids = bakåtkompatibelt (alla aktiva).
  const visibleTasks = useMemo(() => {
    if (!currentObject) return [];
    const allowed = currentObject.task_ids || [];
    const favs = currentObject.favorite_task_ids || [];
    const pool = allowed.length === 0
      ? tasks
      : tasks.filter((t) => allowed.includes(t.task_id));
    const favList = favs
      .map((fid) => pool.find((t) => t.task_id === fid))
      .filter(Boolean);
    const rest = pool.filter((t) => !favs.includes(t.task_id));
    return [...favList, ...rest];
  }, [currentObject, tasks]);

  // Klick på objekt: byter selectedObject och förväljer första favoriten
  const pickObject = (objectId) => {
    setSelectedObject(objectId);
    const obj = objects.find((o) => o.object_id === objectId);
    const favs = obj?.favorite_task_ids || [];
    const allowed = obj?.task_ids || [];
    const firstFav = favs.find((fid) => tasks.some((t) => t.task_id === fid));
    if (firstFav) {
      setSelectedTask(firstFav);
    } else if (selectedTask && allowed.length > 0 && !allowed.includes(selectedTask)) {
      setSelectedTask("");
    } else if (!selectedTask) {
      setSelectedTask("");
    }
  };

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Auto-spara avvikelse-texten på den aktiva rapporten så inget försvinner
  // om användaren stänger tabben, laddar om, eller sessionen förnyas innan
  // utstämpling. Debouncar 1.2s efter senaste tangenttryck.
  const deviationRef = useRef("");
  const deviationSaveTimer = useRef(null);
  const lastSavedDeviation = useRef("");
  const [deviationSaveState, setDeviationSaveState] = useState("idle");
  const [deviationSavedAt, setDeviationSavedAt] = useState(null);

  // Samma auto-save för Notering-fältet.
  const noteRef = useRef("");
  const noteSaveTimer = useRef(null);
  const lastSavedNote = useRef("");
  const [noteSaveState, setNoteSaveState] = useState("idle");
  const [noteSavedAt, setNoteSavedAt] = useState(null);

  useEffect(() => {
    deviationRef.current = deviation;
    if (!active?.report_id) return;
    if (deviation === lastSavedDeviation.current) return;
    setDeviationSaveState("saving");
    if (deviationSaveTimer.current) clearTimeout(deviationSaveTimer.current);
    deviationSaveTimer.current = setTimeout(async () => {
      try {
        await api.patch(`/reports/${active.report_id}`, { deviation });
        lastSavedDeviation.current = deviation;
        setDeviationSavedAt(new Date());
        setDeviationSaveState("saved");
      } catch (e) {
        setDeviationSaveState("error");
      }
    }, 1200);
    return () => {
      if (deviationSaveTimer.current) clearTimeout(deviationSaveTimer.current);
    };
  }, [deviation, active?.report_id]);

  useEffect(() => {
    noteRef.current = note;
    if (!active?.report_id) return;
    if (note === lastSavedNote.current) return;
    setNoteSaveState("saving");
    if (noteSaveTimer.current) clearTimeout(noteSaveTimer.current);
    noteSaveTimer.current = setTimeout(async () => {
      try {
        await api.patch(`/reports/${active.report_id}`, { note });
        lastSavedNote.current = note;
        setNoteSavedAt(new Date());
        setNoteSaveState("saved");
      } catch (e) {
        setNoteSaveState("error");
      }
    }, 1200);
    return () => {
      if (noteSaveTimer.current) clearTimeout(noteSaveTimer.current);
    };
  }, [note, active?.report_id]);

  // Extra säkerhet: när användaren är på väg att lämna sidan, gör en sista
  // synkron försök att spara via sendBeacon-liknande fetch med keepalive.
  useEffect(() => {
    const flush = () => {
      if (!active?.report_id) return;
      const url = `${api.defaults.baseURL}/reports/${active.report_id}`;
      const payload = {};
      if (deviationRef.current !== lastSavedDeviation.current) {
        payload.deviation = deviationRef.current;
      }
      if (noteRef.current !== lastSavedNote.current) {
        payload.note = noteRef.current;
      }
      if (Object.keys(payload).length === 0) return;
      try {
        fetch(url, {
          method: "PATCH",
          credentials: "include",
          keepalive: true,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      } catch {}
    };
    window.addEventListener("beforeunload", flush);
    window.addEventListener("pagehide", flush);
    return () => {
      window.removeEventListener("beforeunload", flush);
      window.removeEventListener("pagehide", flush);
    };
  }, [active?.report_id]);

  const refreshActiveFileCount = async (reportId) => {
    if (!reportId) {
      setActiveReportFileCount(0);
      return;
    }
    try {
      const r = await api.get(`/time-reports/${reportId}/files`);
      setActiveReportFileCount(Array.isArray(r.data) ? r.data.length : 0);
    } catch {
      setActiveReportFileCount(0);
    }
  };

  const load = async () => {
    try {
      const [o, t, a, n, fc] = await Promise.all([
        api.get("/objects"),
        api.get("/tasks"),
        api.get("/reports/active"),
        api.get("/reports/last-auto-close-notification"),
        api.get("/tasks/file-counts").catch(() => ({ data: {} })),
      ]);
      setObjects(o.data);
      setTasks(t.data.filter((x) => x.active));
      setActive(a.data);
      // Återställ avvikelse-texten från servern om användaren har skrivit något
      // tidigare på detta pass (auto-sparad via PATCH). Sätter bara om texten är
      // tom lokalt så vi inte skriver över pågående inmatning från användaren.
      if (a.data?.deviation && !deviationRef.current) {
        setDeviation(a.data.deviation);
        deviationRef.current = a.data.deviation;
        lastSavedDeviation.current = a.data.deviation;
        setDeviationSaveState("saved");
        setDeviationSavedAt(new Date());
      } else if (!a.data) {
        setDeviation("");
        deviationRef.current = "";
        lastSavedDeviation.current = "";
        setDeviationSaveState("idle");
        setDeviationSavedAt(null);
      }
      if (a.data?.note && !noteRef.current) {
        setNote(a.data.note);
        noteRef.current = a.data.note;
        lastSavedNote.current = a.data.note;
        setNoteSaveState("saved");
        setNoteSavedAt(new Date());
      } else if (!a.data) {
        setNote("");
        noteRef.current = "";
        lastSavedNote.current = "";
        setNoteSaveState("idle");
        setNoteSavedAt(null);
      }
      setTaskFileCounts(fc.data || {});
      // Räkna om bilagor på det aktiva passet (så knappen visar "📎 N")
      refreshActiveFileCount(a.data?.report_id);
      const dismissedId = localStorage.getItem(DISMISSED_KEY);
      if (n.data && n.data.report_id !== dismissedId) {
        setAutoCloseNotice(n.data);
      } else {
        setAutoCloseNotice(null);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const dismissAutoCloseNotice = () => {
    if (autoCloseNotice?.report_id) {
      localStorage.setItem(DISMISSED_KEY, autoCloseNotice.report_id);
    }
    setAutoCloseNotice(null);
  };

  useEffect(() => {
    load();
  }, []);

  const activeStart = active ? new Date(active.start_time) : null;
  const liveDuration = useMemo(() => {
    if (!activeStart) return 0;
    return (now.getTime() - activeStart.getTime()) / 1000;
  }, [now, activeStart]);

  const clockIn = async () => {
    if (!selectedObject || !selectedTask) {
      toast.error("Välj både objekt och moment");
      return;
    }
    try {
      setSubmitting(true);
      await api.post("/reports/clock-in", {
        object_id: selectedObject,
        task_id: selectedTask,
        note,
      });
      toast.success("Instämplad!");
      setNote("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Något gick fel");
    } finally {
      setSubmitting(false);
    }
  };

  const clockOut = async () => {
    try {
      setSubmitting(true);
      const trimmedDev = (deviation || "").trim();
      const trimmedNote = (note || "").trim();
      // Både avvikelse och notering är redan auto-sparade via PATCH. Om
      // användaren precis skrivit och inte hunnit debouncea — säkerställ att
      // sista tangenttrycket når servern INNAN clock-out.
      if (active?.report_id) {
        const patchPayload = {};
        if (trimmedDev && deviation !== lastSavedDeviation.current) patchPayload.deviation = deviation;
        if (note !== lastSavedNote.current) patchPayload.note = note;
        if (Object.keys(patchPayload).length > 0) {
          try {
            await api.patch(`/reports/${active.report_id}`, patchPayload);
            if ("deviation" in patchPayload) lastSavedDeviation.current = deviation;
            if ("note" in patchPayload) lastSavedNote.current = note;
          } catch {}
        }
      }
      // Skicka tomt till clock-out så backend inte lägger till texten igen.
      await api.post("/reports/clock-out", { note: "", deviation: "" });
      const hadDeviation = trimmedDev.length > 0;
      toast.success(
        hadDeviation
          ? "Utstämplad · avvikelse sparad"
          : "Utstämplad"
      );
      setNote("");
      setDeviation("");
      lastSavedDeviation.current = "";
      deviationRef.current = "";
      setDeviationSaveState("idle");
      setDeviationSavedAt(null);
      lastSavedNote.current = "";
      noteRef.current = "";
      setNoteSaveState("idle");
      setNoteSavedAt(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte stämpla ut");
    } finally {
      setSubmitting(false);
    }
  };

  const switchTask = async (taskId) => {
    if (!active || taskId === active.task_id || submitting) return;
    try {
      setSubmitting(true);
      await api.post("/reports/switch-task", { task_id: taskId, note, deviation });
      const hadDeviation = deviation.trim().length > 0;
      toast.success(
        hadDeviation
          ? "Moment bytt · avvikelse sparad på föregående moment"
          : "Moment bytt – fortsätt köra"
      );
      setNote("");
      setDeviation("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte byta moment");
    } finally {
      setSubmitting(false);
    }
  };

  const timeStr = now.toLocaleTimeString("sv-SE", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const dateStr = now.toLocaleDateString("sv-SE", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div className="space-y-10" data-testid="stamp-page">
      <WorkdayClock onChange={load} />

      {autoCloseNotice && (
        <div
          data-testid="auto-close-banner"
          className="tk-card p-5 border-l-4 border-[var(--brand-rust)] bg-[var(--brand-rust)]/5 flex items-start gap-3"
        >
          <Warning size={22} weight="fill" className="text-[var(--brand-rust)] mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-heading text-base">
              {autoCloseNotice.force_closed
                ? "Ditt senaste pass tvångs-utstämplades av admin"
                : autoCloseNotice.close_reason === "workday_out"
                ? "Objektet stängdes när du stämplade ut arbetsdagen"
                : "Ditt senaste pass auto-utstämplades"}
            </div>
            <div className="text-sm text-text-muted mt-1">
              {autoCloseNotice.object_name} · {autoCloseNotice.task_name} ·{" "}
              {new Date(autoCloseNotice.start_time).toLocaleString("sv-SE", {
                day: "2-digit",
                month: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
              })}
              {" → "}
              {autoCloseNotice.end_time &&
                new Date(autoCloseNotice.end_time).toLocaleString("sv-SE", {
                  day: "2-digit",
                  month: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
            </div>
            <div className="text-xs text-text-muted mt-2">
              {autoCloseNotice.force_closed
                ? "Kontakta admin om tiden behöver justeras."
                : autoCloseNotice.close_reason === "workday_out"
                ? "När du stämplade ut arbetsdagen stängdes även detta objekt automatiskt (regel: en arbetsdag omfattar allt objektarbete). Kontrollera tiden i Rapporter."
                : "Passet stängdes automatiskt (vid midnatt eller efter 12 timmar). Kontrollera tiden i Rapporter och justera om det behövs."}
            </div>
          </div>
          <button
            data-testid="auto-close-banner-dismiss"
            onClick={dismissAutoCloseNotice}
            className="shrink-0 p-1 rounded hover:bg-surface-hover text-text-muted"
            title="Stäng meddelandet"
            aria-label="Stäng"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {taskMediaFor && (
        <MediaGalleryModal
          basePath={`/tasks/${taskMediaFor.task_id}`}
          title={`Instruktioner & bilagor — ${taskMediaFor.name}`}
          onClose={() => setTaskMediaFor(null)}
          onChanged={() => load()}
          canDelete={(f) => user?.role === "admin"}
        />
      )}

      {activeMediaOpen && active && (
        <MediaGalleryModal
          basePath={`/time-reports/${active.report_id}`}
          title={`Foto & film på passet — ${active.object_name || ""} · ${active.task_name || ""}`}
          onClose={() => {
            setActiveMediaOpen(false);
            refreshActiveFileCount(active.report_id);
          }}
          onChanged={() => refreshActiveFileCount(active.report_id)}
          canDelete={(f) => user?.role === "admin" || f?.uploaded_by === user?.user_id}
        />
      )}

      <header>
        <div className="text-xs uppercase tracking-[0.2em] text-text-muted">
          Hej {user?.name?.split(" ")[0]} ·{" "}
          <span className="capitalize">{dateStr}</span>
        </div>
        <h1 className="font-heading text-5xl md:text-7xl tracking-tighter mt-3 tabular-nums" data-testid="live-clock">
          {timeStr}
        </h1>
      </header>

      {active ? (
        <div className="tk-card p-8" data-testid="active-session-card">
          <div className="flex items-center gap-2 text-status-active flex-wrap">
            <span className="tk-pill tk-pill-active">
              <span className="w-2 h-2 rounded-full bg-[var(--status-active)] animate-pulse" />
              Aktiv stämpling
            </span>
            <button
              type="button"
              data-testid="active-attach-media-btn"
              onClick={() => setActiveMediaOpen(true)}
              className="ml-auto tk-btn-outline !text-xs !px-3 !py-1.5 inline-flex items-center gap-1.5"
              title="Bifoga foto eller film på detta pass"
            >
              <Paperclip size={14} />
              {activeReportFileCount > 0
                ? `Foto/film · ${activeReportFileCount}`
                : "Bifoga foto/film"}
            </button>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-6">
            <div>
              <div className="text-xs uppercase tracking-wider text-text-muted">Objekt</div>
              <div className="font-heading text-2xl mt-1">{active.object_name}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-text-muted">Moment</div>
              <div className="font-heading text-2xl mt-1 flex items-center gap-2">
                <span>{active.task_name}</span>
                {taskFileCounts[active.task_id] > 0 && (() => {
                  const tk = tasks.find((x) => x.task_id === active.task_id);
                  return tk ? (
                    <button
                      data-testid="active-task-media-btn"
                      onClick={() => setTaskMediaFor(tk)}
                      className="tk-btn-outline !text-xs !px-2 !py-1 inline-flex items-center gap-1"
                      title="Instruktioner & bilagor för momentet"
                    >
                      <Paperclip size={12} />
                      {taskFileCounts[active.task_id]}
                    </button>
                  ) : null;
                })()}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-text-muted">Tid på objekt</div>
              <div className="font-heading text-4xl mt-1 tabular-nums" data-testid="live-duration">
                {formatDuration(liveDuration)}
              </div>
            </div>
          </div>
          <div className="mt-6">
            <label className="text-xs uppercase tracking-wider text-text-muted">
              Byt moment
            </label>
            {(() => {
              const activeObj = objects.find((o) => o.object_id === active.object_id);
              const allowed = activeObj?.task_ids || [];
              const favs = activeObj?.favorite_task_ids || [];
              const pool = allowed.length === 0
                ? tasks
                : tasks.filter((t) => allowed.includes(t.task_id));
              const favList = favs
                .map((fid) => pool.find((t) => t.task_id === fid))
                .filter(Boolean);
              const rest = pool.filter((t) => !favs.includes(t.task_id));
              const switchTasks = [...favList, ...rest];
              if (switchTasks.length === 0) {
                return (
                  <p className="text-sm text-text-muted italic mt-3">
                    Inga moment kopplade till detta objekt.
                  </p>
                );
              }
              return (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="switch-task-chips">
                  {switchTasks.map((t) => {
                    const isCurrent = t.task_id === active.task_id;
                    const isFav = favs.includes(t.task_id);
                    return (
                      <button
                        key={t.task_id}
                        data-testid={`switch-task-chip-${t.task_id}`}
                        onClick={() => switchTask(t.task_id)}
                        disabled={submitting || isCurrent}
                        title={isCurrent ? "Nuvarande moment" : `Byt till ${t.name}`}
                        className={`px-4 py-2 rounded-full text-sm border transition-all inline-flex items-center gap-1.5 ${
                          isCurrent
                            ? "text-white cursor-default"
                            : "text-text-primary border-app hover:bg-surface-hover"
                        } ${submitting && !isCurrent ? "opacity-50" : ""}`}
                        style={
                          isCurrent
                            ? { background: t.color || "#C25934", borderColor: "transparent" }
                            : {}
                        }
                      >
                        {isCurrent && (
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-white align-middle animate-pulse" />
                        )}
                        {isFav && !isCurrent && (
                          <Star size={13} weight="fill" className="text-[var(--brand-rust)]" />
                        )}
                        {t.name}
                      </button>
                    );
                  })}
                </div>
              );
            })()}
            <p className="text-xs text-text-muted mt-2">
              Klicka på ett annat moment för att byta utan att stämpla ut.
            </p>
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between gap-2">
              <label className="text-xs uppercase tracking-wider text-text-muted">
                Notering (frivilligt)
              </label>
              {active && (
                <DeviationSaveIndicator
                  state={noteSaveState}
                  savedAt={noteSavedAt}
                  hasText={(note || "").trim().length > 0}
                />
              )}
            </div>
            <textarea
              data-testid="note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="T.ex. färdigt skikt, lunchpaus..."
              className="tk-input mt-2"
            />
          </div>
          <div className="mt-4 border border-[var(--brand-rust)]/30 rounded-lg p-4 bg-[var(--brand-rust)]/5">
            <label className="text-xs uppercase tracking-wider text-[var(--brand-rust)] flex items-center gap-1.5">
              <span aria-hidden>⚠</span> Avvikelse på momentet (frivilligt)
            </label>
            <textarea
              data-testid="deviation-input"
              value={deviation}
              onChange={(e) => setDeviation(e.target.value)}
              rows={2}
              placeholder="Beskriv om något strulade — t.ex. 'Fel dimension på räckesstolpe, kunden godkände muntligt'"
              className="tk-input mt-2 border-[var(--brand-rust)]/40 focus:border-[var(--brand-rust)]"
            />
            <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
              <p className="text-[11px] text-text-muted flex items-center gap-2">
                <span>Sparas på detta moment och kommer med i objektets avvikelselista.</span>
                {active && (
                  <DeviationSaveIndicator
                    state={deviationSaveState}
                    savedAt={deviationSavedAt}
                    hasText={(deviation || "").trim().length > 0}
                  />
                )}
              </p>
              <button
                type="button"
                data-testid="deviation-attach-inline-btn"
                onClick={() => setActiveMediaOpen(true)}
                className="tk-btn-outline !text-xs !px-2.5 !py-1 inline-flex items-center gap-1"
                title="Bifoga foto eller film på passet (och avvikelsen)"
              >
                <Paperclip size={12} /> Bifoga foto/film
                {activeReportFileCount > 0 ? ` · ${activeReportFileCount}` : ""}
              </button>
            </div>
          </div>

          <TaskLogPanel
            task_id={active.task_id}
            task_name={active.task_name}
            currentUser={user}
            object_id={active.object_id}
            object_name={active.object_name}
          />
          <button
            data-testid="clock-out-button"
            disabled={submitting}
            onClick={clockOut}
            className="tk-btn-primary mt-6 inline-flex items-center gap-2"
            style={{ background: "#1a2e25" }}
          >
            <Stop size={20} weight="fill" /> Stämpla ut
          </button>
        </div>
      ) : (
        <div className="tk-card p-8">
          <h2 className="font-heading text-2xl">Stämpla in</h2>
          <p className="text-sm text-text-muted mt-1">
            Välj objekt och moment, sen tryck in.
          </p>

          <div className="grid md:grid-cols-2 gap-6 mt-6">
            <div>
              <label className="text-xs uppercase tracking-wider text-text-muted">
                Objekt
              </label>
              {objects.length === 0 ? (
                <div className="mt-2 text-sm text-text-muted italic">
                  Inga objekt finns. Admin kan skapa via Objekt-sidan.
                </div>
              ) : (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-72 overflow-auto">
                  {objects
                    .filter((o) => o.status !== "completed" && o.status !== "archived")
                    .map((o) => (
                      <button
                        key={o.object_id}
                        data-testid={`object-card-${o.object_id}`}
                        onClick={() => pickObject(o.object_id)}
                        className={`text-left p-4 rounded-xl border transition-all ${
                          selectedObject === o.object_id
                            ? "border-[var(--primary)] bg-[#fff4ef]"
                            : "border-app hover:bg-surface-hover"
                        }`}
                      >
                        <div className="font-heading text-base">{o.name}</div>
                        {o.description && (
                          <div className="text-xs text-text-muted mt-1 whitespace-pre-line break-words">
                            {o.description}
                          </div>
                        )}
                      </button>
                    ))}
                </div>
              )}
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-text-muted">
                Moment
              </label>
              {!selectedObject ? (
                <p className="text-sm text-text-muted italic mt-3" data-testid="task-pick-hint">
                  Välj ett objekt först för att se tillgängliga moment.
                </p>
              ) : visibleTasks.length === 0 ? (
                <p className="text-sm text-text-muted italic mt-3" data-testid="task-pick-empty">
                  Inga moment är kopplade till detta objekt. Admin kan lägga till via Objekt-sidan.
                </p>
              ) : (
                <div className="mt-3 flex flex-wrap gap-2">
                  {visibleTasks.map((t) => {
                    const isFav = (currentObject?.favorite_task_ids || []).includes(t.task_id);
                    const isSelected = selectedTask === t.task_id;
                    return (
                      <button
                        key={t.task_id}
                        data-testid={`task-chip-${t.task_id}`}
                        onClick={() => setSelectedTask(t.task_id)}
                        className={`px-4 py-2 rounded-full text-sm border transition-all inline-flex items-center gap-1.5 ${
                          isSelected
                            ? "text-white"
                            : "text-text-primary border-app hover:bg-surface-hover"
                        }`}
                        style={
                          isSelected
                            ? { background: t.color || "#C25934", borderColor: "transparent" }
                            : {}
                        }
                      >
                        {isFav && (
                          <Star
                            size={13}
                            weight="fill"
                            data-testid={`task-chip-fav-${t.task_id}`}
                            className={isSelected ? "text-white" : "text-[var(--brand-rust)]"}
                          />
                        )}
                        {t.name}
                        {taskFileCounts[t.task_id] > 0 && (
                          <span
                            role="button"
                            tabIndex={0}
                            aria-label="Instruktioner"
                            data-testid={`task-chip-media-${t.task_id}`}
                            onClick={(e) => { e.stopPropagation(); setTaskMediaFor(t); }}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); setTaskMediaFor(t); } }}
                            className={`ml-1 inline-flex items-center gap-0.5 text-[10px] rounded-full px-1.5 py-0.5 cursor-pointer ${
                              isSelected ? "bg-white/25 text-white" : "bg-app text-text-muted hover:text-brand"
                            }`}
                            title="Se instruktioner (foto/film)"
                          >
                            <Paperclip size={10} />
                            {taskFileCounts[t.task_id]}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              <label className="text-xs uppercase tracking-wider text-text-muted block mt-6">
                Notering (frivilligt)
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder="Detaljer om passet..."
                className="tk-input mt-2"
              />
            </div>
          </div>

          <button
            data-testid="clock-in-button"
            disabled={submitting || !selectedObject || !selectedTask}
            onClick={clockIn}
            className="tk-btn-primary mt-8 inline-flex items-center gap-2"
          >
            <Play size={20} weight="fill" /> Stämpla in
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Diskret indikator som visar att avvikelse-texten sparas i realtid.
 * Uppdaterar sig själv varje 30s så "sparad 17:23" inte visar en fastfrusen
 * tidpunkt när fönstret legat öppet en stund.
 */
function DeviationSaveIndicator({ state, savedAt, hasText }) {
  // Tvinga re-render varje 30s så tidstämpeln inte känns död
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  if (state === "saving") {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-text-muted italic">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-text-muted animate-pulse" />
        Sparar…
      </span>
    );
  }
  if (state === "error") {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-amber-700 font-medium">
        <span aria-hidden>⚠</span> Kunde inte spara automatiskt — försök igen
      </span>
    );
  }
  if (state === "saved" && hasText && savedAt) {
    const now = Date.now();
    const diffSec = Math.max(0, Math.floor((now - savedAt.getTime()) / 1000));
    let label;
    if (diffSec < 5) label = "just nu";
    else if (diffSec < 60) label = `för ${diffSec}s sedan`;
    else if (diffSec < 3600) label = `för ${Math.floor(diffSec / 60)} min sedan`;
    else label = savedAt.toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" });
    return (
      <span
        className="inline-flex items-center gap-1 text-[11px] text-emerald-700 font-medium"
        data-testid="deviation-save-indicator"
        title={`Auto-sparad kl ${savedAt.toLocaleTimeString("sv-SE")}`}
      >
        <span aria-hidden>✓</span> Sparad {label}
      </span>
    );
  }
  return null;
}

